import angular from 'angular';

import {
  ADVERSE_EVENT_ATTESTED,
  USAGE_DATA_UPDATED,
  COMPANY_ADDED,
  USER_ADDED,
  SYSTEM_MESSAGE_CHANGED,
  COMPANY_MESSAGE_CHANGED,
  REGULATOR_ADDED
} from 'common/messages';
import templateUrl from './dashboard.html';

export default {
  templateUrl,
  bindings: {
    adminMessage: '<',
    alerts: '<',
    companyMessage: '<',
    flex: '<',
    layout: '<'
  },
  controller: class DashboardComponent {
    constructor($mdDialog, $scope, $state, AccountService,
      AdverseEventService, AdverseEventsService, AuthService, CompanyService,
      LoadingService, MessageService, RegulatorService,
      ReportService, ToastService, UsageDataService) {
      'ngInject';

      Object.assign(this, {
        $mdDialog,
        $scope,
        $state,
        AccountService,
        AdverseEventService,
        AdverseEventsService,
        AuthService,
        CompanyService,
        LoadingService,
        MessageService,
        RegulatorService,
        ReportService,
        ToastService,
        UsageDataService
      });
    }

    showUsages(quarterOnly) {
      let promise;
      if (quarterOnly) {
        promise = this.UsageDataService.getMissingQuarterUsageData();
      } else {
        promise = this.UsageDataService.getMissingUsageData();
      }

      promise.then((r) => {
        const usages = r.data;

        if (usages && usages.length) {
          const scope = angular.extend(this.$scope.$new(true), {
            usages,
            onUsageDataUpdated: $event => this.onUsageDataUpdated($event)
          });

          this.$mdDialog.show({
            clickOutsideToClose: true,
            fullscreen: true,
            scope,
            template: `
          <md-dialog class="flex-85" style="max-height: 90%">
            <md-dialog-content>
              <dialog-close/>
              <bulk-usage-data
                usages="usages"
                on-usages-submitted="onUsageDataUpdated($event)">
              </bulk-usage-data>
            </md-dialog-content>
          </md-dialog>
          `
          });
        } else {
          this.$mdDialog.show(
            this.$mdDialog.alert()
              .clickOutsideToClose(true)
              .textContent('You are currently in compliance. No further action is required.')
              .ariaLabel('No action required')
              .ok('OK')
          );
        }
      }).catch(error => this.ToastService.showError(error));
    }

    onUsageDataUpdated($event) {
      if ($event && $event.usages) {
        this.UsageDataService.updateMissingUsageData($event.usages)
          .then(() => {
            this.ToastService.showSuccess(USAGE_DATA_UPDATED);
            this.$mdDialog.hide();
            this.$state.reload();
          }).catch(error => this.ToastService.showError(error));
      }
    }

    showAttestment() {
      this.AdverseEventsService.getOpenAdverseEventsPreviousMonth().then((r) => {
        const adverseEvents = r.data;
        const scope = angular.extend(this.$scope.$new(true), {
          adverseEvents,
          onAttested: $event => this.onAttested($event)
        });

        this.$mdDialog.show({
          clickOutsideToClose: true,
          fullscreen: true,
          scope,
          template: `
            <md-dialog class="flex-75" style="max-height: 75%">
              <md-dialog-content>
                <dialog-close/>
                <attest
                  adverse-events="adverseEvents"
                  on-attested="onAttested($event)">
                </attest>
              </md-dialog-content>
            </md-dialog>
          `
        });
      }).catch(error => this.ToastService.showError(error));
    }

    onAttested({ signature }) {
      if (signature) {
        this.AdverseEventService.attest(signature)
          .then(() => {
            this.ToastService.showSuccess(ADVERSE_EVENT_ATTESTED);
            this.$mdDialog.hide();
            this.$state.reload();
          }).catch(error => this.ToastService.showError(error));
      }
    }

    showCompanyAdd() {
      const scope = angular.extend(this.$scope.$new(true), {
        onCompanyAdded: $event => this.onCompanyAdded($event)
      });

      this.$mdDialog.show({
        clickOutsideToClose: true,
        fullscreen: true,
        scope,
        template: `
          <md-dialog class="flex-50">
            <md-dialog-content>
              <dialog-close/>
              <company-add-form
                on-company-changed="onCompanyAdded($event)">
              </company-add-form>
            </md-dialog-content>
          </md-dialog>
          `
      });
    }

    onCompanyAdded({ company }) {
      if (company) {
        this.CompanyService.add(company)
          .then((r) => {
            this.$mdDialog.hide();
            this.ToastService.showSuccess(COMPANY_ADDED);

            const companyId = r.data.Id;
            this.$state.go('company', { id: companyId });
          }).catch(error => this.ToastService.showError(error));
      }
    }

    showUserAdd() {
      const scope = angular.extend(this.$scope.$new(true), {
        accessLevels: this.AuthService.getAccessLevels(),
        onUserAdded: $event => this.onUserAdded($event)
      });

      this.$mdDialog.show({
        clickOutsideToClose: true,
        fullscreen: true,
        scope,
        template: `
        <md-dialog class="flex-50">
          <md-dialog-content>
            <dialog-close/>
            <user-add-form
              require-user-type-selection="true"
              access-levels="accessLevels"
              on-user-changed="onUserAdded($event)">
            </user-add-form>
          </md-dialog-content>
        </md-dialog>
        `
      });
    }

    onUserAdded({ user }) {
      if (user) {
        this.AccountService.addAccount(user)
          .then(() => {
            this.$mdDialog.hide();
            this.ToastService.showSuccess(USER_ADDED);
          }).catch(error => this.ToastService.showError(error));
      }
    }

    alertClicked({ alert }) {
      if (alert) {
        if (alert.AlertTypeID === 1) {
          this.$state.go('products', { search: 'missing_info' });
        } else if (alert.AlertTypeID === 2) {
          this.$state.go('adverseEvents', { search: 'status_open' });
        } else if (alert.AlertTypeID === 3) {
          if (this.AuthService.isSysAdmin) {
            this.$state.go('products', { search: 'missing_sales' });
          } else {
            this.showUsages(false);
          }
        } else if (alert.AlertTypeID === 4) {
          if (this.AuthService.isSysAdmin) {
            this.$state.go('memberCompliance');
          } else {
            this.showAttestment();
          }
        }
      }
    }

    showEditMessage(messageType) {
      const action = messageType === 'SYSTEM' ?
        this.onSystemMessageChanged.bind(this) :
        this.onCompanyMessageChanged.bind(this);

      const scope = angular.extend(this.$scope.$new(true), {
        message: messageType === 'SYSTEM' ? this.adminMessage : this.companyMessage,
        title: messageType === 'SYSTEM' ? 'NASC Message' : 'Company Message',
        onMessageChanged: $event => action($event)
      });

      this.$mdDialog.show({
        clickOutsideToClose: true,
        fullscreen: true,
        scope,
        template: `
          <md-dialog class="flex-50">
            <md-dialog-content>
              <dialog-close/>
              <message-edit message="message"
                title="title"
                on-message-changed="onMessageChanged($event)">
              </message-edit>
            </md-dialog-content>
          </md-dialog>
          `
      });
    }

    onSystemMessageChanged({ message }) {
      this.MessageService.updateAdminMessage(message)
        .then(() => {
          this.$mdDialog.hide();
          this.ToastService.showSuccess(SYSTEM_MESSAGE_CHANGED);
          this.$state.reload();
        }).catch(error => this.ToastService.showError(error));
    }

    onCompanyMessageChanged({ message }) {
      this.MessageService.updateCompanyMessage(message)
        .then(() => {
          this.$mdDialog.hide();
          this.ToastService.showSuccess(COMPANY_MESSAGE_CHANGED);
          this.$state.reload();
        }).catch(error => this.ToastService.showError(error));
    }

    showRegulatorAdd() {
      const scope = angular.extend(this.$scope.$new(true), {
        onRegulatorAdded: $event => this.onRegulatorAdded($event)
      });

      this.$mdDialog.show({
        clickOutsideToClose: true,
        fullscreen: true,
        scope,
        template: `
          <md-dialog class="flex-50">
            <md-dialog-content>
              <dialog-close/>
              <regulator-add-form
                dialog-title="Add New Regulator"
                submit-title="Add Regulator"
                on-regulator-changed="onRegulatorAdded($event)">
              </regulator-add-form>
            </md-dialog-content>
          </md-dialog>
          `
      });
    }

    onRegulatorAdded({ regulator }) {
      if (regulator) {
        this.RegulatorService.add(regulator)
          .then(() => {
            this.ToastService.showSuccess(REGULATOR_ADDED);
            this.$mdDialog.hide();
            this.$state.reload();
          }).catch(error => this.ToastService.showError(error));
      }
    }
  }
};



// WEBPACK FOOTER //
// ./components/app/dashboard/dashboard.component.js